import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Users, Briefcase, FileText, Building2 } from 'lucide-react';
import styles from './DepartmentManagement.module.css';
import DepartmentApplicationsTab from '../components/DepartmentApplicationsTab';
import DepartmentRosterTab from '../components/DepartmentRosterTab';
import DepartmentPositionsTab from '../components/DepartmentPositionsTab';

type TabType = 'applications' | 'roster' | 'positions';

interface Department {
  id: string;
  name: string;
}

const DEPARTMENTS: Department[] = [
  { id: 'command', name: 'Command' },
  { id: 'operations', name: 'Operations' },
  { id: 'intelligence', name: 'Intelligence' },
  { id: 'logistics', name: 'Logistics' },
  { id: 'security', name: 'Security' },
  { id: 'medical', name: 'Medical' },
  { id: 'engineering', name: 'Engineering' },
  { id: 'communications', name: 'Communications' },
];

const DepartmentManagement: React.FC = () => {
  const { department } = useParams<{ department: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('applications');
  const [selectedDepartment, setSelectedDepartment] = useState<string>(department || '');

  // Check if user is CEO or COO
  const isCeoOrCoo = (): boolean => {
    if (!user) return false;
    const titles = [
      user.primaryPositionTitle,
      user.secondaryPositionTitle,
      user.tertiaryPositionTitle
    ].filter(Boolean).map((t: string) => t.toLowerCase());
    
    console.log('🔍 Checking CEO/COO - Titles:', titles);
    
    return titles.includes('ceo') || titles.includes('chief executive officer') || 
           titles.includes('coo') || titles.includes('chief operating officer');
  };

  // Check if user is C-Suite (Tier 4)
  const isCSuite = (): boolean => {
    if (!user) return false;
    const result = user.positionTier === 4;
    console.log('🔍 Checking C-Suite - Result:', result, 'Tier:', user.positionTier);
    return result;
  };

  // Get user's department from their position
  const getUserDepartment = (): string | null => {
    if (!user) return null;
    
    // Check primary position first
    if (user.primaryPosition) {
      console.log('🔍 Found department in primary:', user.primaryPosition);
      return user.primaryPosition.toLowerCase();
    }
    
    // Fall back to secondary
    if (user.secondaryPosition) {
      console.log('🔍 Found department in secondary:', user.secondaryPosition);
      return user.secondaryPosition.toLowerCase();
    }
    
    // Fall back to tertiary
    if (user.tertiaryPosition) {
      console.log('🔍 Found department in tertiary:', user.tertiaryPosition);
      return user.tertiaryPosition.toLowerCase();
    }
    
    console.log('🔍 No department found');
    return null;
  };

  // Check if user can access this department
  const canAccessDepartment = (dept: string): boolean => {
    // Admin can access any department
    if (user?.role === 'Admin') return true;
    // CEO/COO can access any department
    if (isCeoOrCoo()) return true;
    // C-Suite leaders can access their department
    if (isCSuite() && getUserDepartment() === dept.toLowerCase()) return true;
    // Other leaders can access their department
    return getUserDepartment() === dept.toLowerCase();
  };

  // Validate access
  useEffect(() => {
    if (department && user) {
      // For admins without positions, allow access
      if (user.role === 'Admin') return;
      
      if (!canAccessDepartment(department)) {
        // User doesn't have access, redirect to their department or home
        const userDept = getUserDepartment();
        if (userDept) {
          navigate(`/departments/${userDept}/manage`);
        } else {
          navigate('/home');
        }
      }
    }
  }, [department, user]);

  // Update selected department when URL changes
  useEffect(() => {
    if (department) {
      setSelectedDepartment(department);
    }
  }, [department]);

  // Handle department selection (for CEO/COO)
  const handleDepartmentChange = (deptId: string) => {
    navigate(`/departments/${deptId}/manage`);
  };

  // Get current department name
  const currentDept = DEPARTMENTS.find(d => d.id === selectedDepartment);
  
  // Get display name with proper capitalization
  const getDepartmentDisplayName = (): string => {
    if (currentDept) return currentDept.name;
    // Fallback: capitalize the URL param
    if (selectedDepartment) {
      return selectedDepartment.charAt(0).toUpperCase() + selectedDepartment.slice(1);
    }
    return 'Department';
  };

  // If no department or user not authenticated, redirect
  if (!user || !department) {
    return <Navigate to="/home" />;
  }

  // If user doesn't have leadership access at all (not admin, not leader role)
  if (user.role !== 'Admin' && user.role !== 'Leader') {
    return <Navigate to="/home" />;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <div className={styles.titleSection}>
            <Building2 size={32} className={styles.titleIcon} />
            <div>
              <h1 className={styles.title}>
                {getDepartmentDisplayName()} Department
              </h1>
              <p className={styles.subtitle}>
                Manage applications, roster, and positions
              </p>
            </div>
          </div>

          {/* CEO/COO/Admin Department Selector */}
          {(isCeoOrCoo() || user?.role === 'Admin') && (
            <div className={styles.departmentSelector}>
              <label htmlFor="dept-select">Department:</label>
              <select
                id="dept-select"
                value={selectedDepartment}
                onChange={(e) => handleDepartmentChange(e.target.value)}
                className={styles.select}
              >
                {DEPARTMENTS.map(dept => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'applications' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('applications')}
          >
            <FileText size={20} />
            Applications
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'roster' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('roster')}
          >
            <Users size={20} />
            Roster
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'positions' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('positions')}
          >
            <Briefcase size={20} />
            Positions
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className={styles.content}>
        {activeTab === 'applications' && (
          <DepartmentApplicationsTab department={selectedDepartment} />
        )}

        {activeTab === 'roster' && (
          <DepartmentRosterTab department={selectedDepartment} />
        )}

        {activeTab === 'positions' && (
          <DepartmentPositionsTab department={selectedDepartment} />
        )}
      </div>
    </div>
  );
};

export default DepartmentManagement;
